import { Injectable } from "@angular/core";
import { IDecideNextStep } from "projects/borrower-app/src/app/services/wizard/next-step-decider/next-step-decider.interface";
import { SingleChoiceQuestionStepComponent } from "projects/borrower-app/src/app/wizard/wizard-steps/single-choice-question-step/single-choice-question-step.component";
import { WizardFlowContext } from "../../../wizard-flow-context.model";
import { SingleChoiceQuestionStep } from "../../single-choice-question-step.model";
import { SingleChoiceQuestionStepConfig } from "../single-choice-question-step-config.model";
import { OPTION_LABEL_DEFAULTS } from "../../option-label-defaults";
import { NextStepDeciderFactory } from "./next-step-decider.factory";
import { StepFactoryBase } from "./step-factory-base";
import { ICreateStep } from "./step-factory.interface";

@Injectable()
export class SingleChoiceQuestionStepFactory extends StepFactoryBase<SingleChoiceQuestionStepConfig> implements ICreateStep {

  constructor(private readonly _nextStepDeciderFactory: NextStepDeciderFactory) {
    super();
  }

  configure(step: SingleChoiceQuestionStep, config: SingleChoiceQuestionStepConfig): void {

    config.title = step.title;
    config.explanation = step.explanation;
    config.heading = step.heading || step.question.text;
    config.groupId = step.groupId;
    config._i18n = step._i18n;

    config.optionsProviderType = step.optionsProviderType;
    config.nameOfDefaultOptionToSetAndSkip = step.nameOfDefaultOptionToSetAndSkip;

    if (config.question && step.question) {
      config.question._i18n = step.question._i18n;
    }

    config.question.options.forEach(optionConfig => {
      const option = step.question.options.find(o => o.name === optionConfig.name);
      if (option) {
        optionConfig.text = option.text;
        optionConfig.explanation = option.explanation;
        optionConfig._i18n = option._i18n;
        optionConfig._defaultsKey = option._defaultsKey;
      }
    });
  }

  create = (config: SingleChoiceQuestionStepConfig, wizardFlowContext: WizardFlowContext): SingleChoiceQuestionStep => {
    const nextStepDecider: IDecideNextStep | undefined = this._nextStepDeciderFactory.create(config.nextStepDeciderConfig,
      wizardFlowContext);
    const step = new SingleChoiceQuestionStep(config.id, config.name,
      config.path, config.question, nextStepDecider, config.fieldToSetWithAnswer, wizardFlowContext, config.contextType, SingleChoiceQuestionStepComponent);
    step.title = config.title;
    step.explanation = config.explanation;
    step.heading = config.heading;
    step.groupId = config.groupId;
    step._i18n = config._i18n;

    step.optionsProviderType = config.optionsProviderType;
    step.nameOfDefaultOptionToSetAndSkip = config.nameOfDefaultOptionToSetAndSkip;
    step.question.text = config.heading || step.question.text;

    if (config.question && step.question) {
      step.question._i18n = config.question._i18n;
    }

    step.question.options.forEach(option => {
      const optionConfig = config.question.options.find(o => o.name === option.name);
      if (optionConfig) {
        option.text = optionConfig.text;
        option.explanation = optionConfig.explanation;
        option._i18n = optionConfig._i18n;
        option._defaultsKey = optionConfig._defaultsKey;
      }
    });

    // Auto-tag options with OPTION_LABEL_DEFAULTS lookup key when we can
    // confidently identify the step. We can't rely on `optionsProviderType`
    // because the server flow config doesn't always persist that field, so
    // we gate by `step.path` + matching option.value against known defaults.
    SingleChoiceQuestionStepFactory._autoTagKnownStepOptions(step);

    return step;
  }

  /**
   * Per-step-path → OPTION_LABEL_DEFAULTS prefix. Scoped tightly to known
   * steps so a random single-choice-question step with an option whose `value`
   * happens to collide with a loanPurpose key won't get mistakenly tagged.
   *
   * Add a new entry here whenever a step's options get built-in translations
   * in OPTION_LABEL_DEFAULTS - saved flows only carry `_defaultsKey` if they
   * were created after the key existed, so without an entry every older flow
   * shows the English labels.
   */
  private static readonly _STEP_PATH_TO_DEFAULTS_PREFIX: Record<string, string> = {
    'loan-purpose': 'loanPurpose',
    'current-app-state': 'appState',
    'occupancy': 'occupancy',
    'property-type': 'propertyType',
    'employment-income': 'income',
    'asset-manual-digital': 'assets',
  };

  private static _autoTagKnownStepOptions(step: SingleChoiceQuestionStep): void {
    if (!step?.question?.options || !step.path) return;
    const prefix = SingleChoiceQuestionStepFactory._STEP_PATH_TO_DEFAULTS_PREFIX[step.path];
    if (!prefix) return;
    step.question.options.forEach(o => {
      if (o._defaultsKey) return;
      // Try value first (for enum-valued options like loanPurpose), then fall
      // back to name (for boolean-valued options where only name identifies it).
      const candidates = [
        o.value != null && o.value !== '' ? `${prefix}.${o.value}` : undefined,
        o.name ? `${prefix}.${o.name}` : undefined,
      ].filter((c): c is string => !!c);
      for (const candidate of candidates) {
        if (OPTION_LABEL_DEFAULTS[candidate]) {
          o._defaultsKey = candidate;
          return;
        }
      }
    });
  }
}
